/*
 * Google Tag Manager for the dashboard, with Consent Mode v2 denied by default.
 *
 * All of it in TypeScript rather than in `index.html`, which is the opposite of
 * the marketing site's approach and for a reason: the container is optional, and
 * a conditional in an HTML template is a `%VITE_GTM_ID%` substitution that
 * leaves an empty script tag and a broken container URL when the variable is
 * unset. Here, no id means none of this runs.
 *
 * The consent story is the same as the marketing site's and is deliberately not
 * shared code: the two repositories build separately and neither depends on the
 * other. What is shared is the storage key and the semantics, so a person who
 * has read one file recognises the other.
 *
 * They do NOT share the stored choice. `maxlayer.cloud` and `app.maxlayer.cloud`
 * are different origins, so local storage does not cross between them and a
 * visitor who accepts on the marketing site is asked again on first sign-in.
 * Sharing it would mean a cookie on the parent domain — which is a tracking
 * cookie set before consent, to record consent.
 */

const configured = import.meta.env.VITE_GTM_ID?.trim();

if (configured && !/^GTM-[A-Z0-9]{4,}$/.test(configured)) {
  throw new Error(
    `VITE_GTM_ID is set to "${configured}", which is not a container id. ` +
      "It must look like GTM-XXXXXXX. Leave it unset to build without a tag manager.",
  );
}

/** The container id, or `null` when this build carries no tag manager. */
export const GTM_ID: string | null = configured || null;

/*
 * Matches the marketing site's key and shape, so the two read as one system.
 *
 * `v2` because the shape changed: v1 held the string "granted" or "denied",
 * from when this was one yes-or-no. Anything still holding a v1 value reads as
 * unanswered and is asked again, which is right — consent to "analytics" is not
 * consent to the three categories that replaced it.
 *
 * The two apps do NOT share the stored value. `maxlayer.cloud` and
 * `app.maxlayer.cloud` are different origins, so local storage does not cross
 * between them and someone who answers on the marketing site is asked again on
 * first sign-in. Sharing it would mean a cookie on the parent domain, written
 * to record that somebody may not want cookies.
 */
export const CONSENT_STORAGE_KEY = "maxlayer.consent.v2";

/** The optional categories, and the Consent Mode signals each one releases. */
export const CONSENT_SIGNALS = {
  performance: ["analytics_storage"],
  functional: ["functionality_storage", "personalization_storage"],
  targeting: ["ad_storage", "ad_user_data", "ad_personalization"],
} as const;

export type ConsentCategory = keyof typeof CONSENT_SIGNALS;

export type ConsentChoices = Record<ConsentCategory, boolean>;

export const CATEGORY_IDS = Object.keys(CONSENT_SIGNALS) as ConsentCategory[];

/** Every category off — the state before anybody has answered. */
export function noConsent(): ConsentChoices {
  return { performance: false, functional: false, targeting: false };
}

/** Every category on. */
export function fullConsent(): ConsentChoices {
  return { performance: true, functional: true, targeting: true };
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** The stored choices, or `null` when the visitor has not answered. */
export function storedConsent(): ConsentChoices | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    /*
     * Read field by field rather than trusting the parse. A value written by an
     * older build, or edited by hand, must not be able to grant a category by
     * being the wrong shape — anything unrecognised reads as off.
     */
    const record = parsed as Record<string, unknown>;
    return {
      performance: record.performance === true,
      functional: record.functional === true,
      targeting: record.targeting === true,
    };
  } catch {
    // Unreadable storage, or something that is not JSON. Treated as unanswered,
    // which leaves every category denied.
    return null;
  }
}

/** Consent Mode wants one flat object of signals, not our three buckets. */
function consentPayload(choices: ConsentChoices): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const id of CATEGORY_IDS) {
    for (const signal of CONSENT_SIGNALS[id]) {
      payload[signal] = choices[id] ? "granted" : "denied";
    }
  }
  return payload;
}

/**
 * Writes the consent defaults and loads the container, in that order.
 *
 * The order is the control, not a preference. A container that loads before the
 * defaults are on the dataLayer runs its tags under Google's own default, which
 * is granted — so a swap here silently turns consent from enforced into
 * decorative.
 *
 * Safe to call more than once; the second call does nothing. React 19's
 * StrictMode double-invokes effects in development, and a container injected
 * twice reports every event twice.
 */
let started = false;

export function startAnalytics(): void {
  if (!GTM_ID || started || typeof window === "undefined") return;
  started = true;

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // `arguments`, not a rest parameter. Google's tags read the raw Arguments
    // object off the dataLayer and an array does not behave the same way.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };

  /*
   * A returning visitor's answer is applied as the *default*, not as an update
   * after the fact. Their first pageview of the session is then measured under
   * the choice they already made, rather than being denied and corrected a few
   * hundred milliseconds later — which loses the tag that mattered and reads as
   * a bug in the data.
   */
  const choices = storedConsent() ?? noConsent();

  window.gtag("consent", "default", {
    ...consentPayload(choices),
    // Not gated: this covers authentication and abuse prevention, which the
    // banner does not offer to switch off.
    security_storage: "granted",
    wait_for_update: 500,
  });
  window.gtag("set", "ads_data_redaction", !choices.targeting);
  window.gtag("set", "url_passthrough", true);

  /*
   * The container's own start event, pushed as a plain object.
   *
   * It went through a `push(...args)` helper that forwarded its arguments
   * array, so what reached the dataLayer was `[{ 'gtm.start': …, event:
   * 'gtm.js' }]` — the event wrapped in an array rather than the event. Tag
   * Manager never registered it, and the whole dashboard reported nothing to
   * Google Analytics for as long as the container had been live: the
   * Initialization trigger still fired and still loaded the Google tag, so
   * every outward sign was of a working setup, and the only symptom was an
   * empty property.
   *
   * The array-wrapped entry is also the one dataLayer entry with no
   * `gtm.uniqueEventId` on it, which is how it was eventually found.
   */
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });

  const script = document.createElement("script");
  script.async = true;
  // The build's CSP nonce. GTM copies it onto every script it injects, which
  // is what lets a strict script-src and a tag manager coexist; see
  // scripts/headers.mjs. Absent in dev, where there is no CSP.
  if (typeof __CSP_NONCE__ === "string") script.nonce = __CSP_NONCE__;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(script);
}

/** Applies a visitor's answer, and remembers it for next time. */
export function recordConsent(choices: ConsentChoices): void {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choices));
  } catch {
    // The choice still governs this session even if it cannot be remembered.
  }

  window.gtag?.("consent", "update", consentPayload(choices));
  window.gtag?.("set", "ads_data_redaction", !choices.targeting);

  /*
   * Pushed after the update rather than before it, so the event describing the
   * choice is itself governed by the choice. Which categories were granted, and
   * nothing else — this is how the banner's own effect is measurable without
   * measuring anybody who declined.
   */
  track("consent_update", {
    object: "consent",
    detail: CATEGORY_IDS.filter((id) => choices[id]).join("+") || "none",
  });
}

/**
 * One `page_view` per navigation.
 *
 * The container must trigger pageviews on this event and *not* on All Pages: a
 * single-page app loads the document once, so All Pages fires on the first
 * route and never again — and with both enabled the first route is counted
 * twice.
 */
export function trackPageView(path: string): void {
  if (!GTM_ID) return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: "page_view",
    page_path: withoutLinker(path),
    page_location: window.location.href,
    page_title: document.title,
  });
}

/**
 * The path without Google's `_gl` linker parameter.
 *
 * `url_passthrough` puts the linker in the URL whenever cookie storage is
 * denied — the default state here, so the common case rather than an edge one —
 * and its value changes on every navigation. Left in, one route reports as
 * hundreds of distinct paths and the pages report is useless. The untouched
 * href still goes out as `page_location`.
 */
function withoutLinker(path: string): string {
  const split = path.indexOf("?");
  if (split === -1) return path;

  const params = new URLSearchParams(path.slice(split + 1));
  params.delete("_gl");
  const query = params.toString();
  return path.slice(0, split) + (query ? `?${query}` : "");
}

/**
 * Who the events belong to.
 *
 * Identifiers only — never the email address, the person's name, or anything
 * from inside a customer's applications. The privacy policy and Annex A of the
 * DPA both say exactly that, so widening this is a documentation change before
 * it is a code change.
 *
 * Pushed as its own event rather than merged into `page_view`, because the
 * organization can change without a navigation: the switcher in the rail
 * re-scopes every page in place.
 */
export function identify(userId: string | null, orgId: string | null): void {
  if (!GTM_ID) return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: "identity",
    user_id: userId,
    org_id: orgId,
  });
}

/**
 * The fields a product event carries. Always all five, which is the point
 * rather than a limitation.
 *
 * Google Tag Manager merges a push into its model instead of replacing it, so a
 * parameter sent on one event and left off the next is still readable when the
 * next one fires. An `api_key_create` inheriting the role from the invitation
 * before it is not a failure anybody notices — the number looks plausible.
 * Sending every field every time, `null` where it does not apply, removes that
 * rather than documenting it.
 *
 * It keeps the container small too: five variables cover every event in the
 * dashboard, where a bespoke parameter per event would be one variable each and
 * a reason not to bother creating it.
 *
 * Nothing here may identify a person. `id` is a resource id and `detail` is a
 * word from a fixed set — an action, a role, an engine, a provider. The privacy
 * policy and Annex A of the DPA both promise identifiers and nothing else, so
 * widening this is a documentation change before it is a code change.
 */
export interface TrackedFields {
  /** What the event is about. */
  object:
    | "account"
    | "api_key"
    | "app"
    | "consent"
    | "database"
    | "domain"
    | "git"
    | "member"
    | "payment"
    | "schedule"
    | "tunnel";
  /** The resource this happened to, when there is one. */
  id?: string | null;
  /** A word from a fixed vocabulary: an action, a role, an engine, a provider. */
  detail?: string | null;
  /** Money, in whole currency units. Only ever set on a payment. */
  value?: number | null;
  /** ISO 4217, alongside `value` and never without it. */
  currency?: string | null;
}

/*
 * An email address is not an identifier this is allowed to send, and the two
 * calls most likely to pass one — inviting a member, registering an account —
 * are exactly the ones with an event beside them. Dropped rather than trusted:
 * review catches this once, a guard catches it every time.
 */
function withoutAddress(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.includes("@") ? null : value;
}

/**
 * A product event.
 *
 * Pushed to the dataLayer for the container to forward, exactly like
 * `page_view` and `identity` — this module never talks to Google Analytics
 * itself, so which property receives an event stays a deployment decision
 * rather than a compiled-in one.
 *
 * A build with no container pushes nothing at all, so a local checkout does not
 * accumulate a dataLayer nobody reads.
 */
export function track(event: string, fields: TrackedFields): void {
  if (!GTM_ID) return;

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event,
    mx_object: fields.object,
    mx_id: withoutAddress(fields.id),
    mx_detail: withoutAddress(fields.detail),
    mx_value: fields.value ?? null,
    mx_currency: fields.currency ?? null,
  });
}

/*
 * How anything else in the app reopens the preferences dialog.
 *
 * An event rather than exported state or a context, because the only caller is
 * a menu item several levels away from the dialog and the alternative is
 * threading a setter through `AppLayout` for one button. It also keeps a single
 * dialog instance: a second `<ConsentPreferences>` rendered next to the menu
 * would be a second source of truth for what is currently in force.
 */
export const CONSENT_OPEN_EVENT = "maxlayer:consent-open";

/** Opens the cookie preferences dialog from anywhere. */
export function openConsentPreferences(): void {
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}
